/**
 * Age-verification consent for products with product.metafields.pdp.agecheck.
 * Persists cart attributes age_declaration / age_declaration_timestamp via /cart/update.js.
 */
var PROJ = typeof PROJ === 'object' ? PROJ : {};

PROJ.ageVerification = {
  ATTR_KEY: 'age_declaration',
  ATTR_VALUE: 'confirmed',
  ATTR_TS_KEY: 'age_declaration_timestamp',
  _pending: null,
  _cartPromise: null,
  _minicartBound: false,

  init: function () {
    var root = document.getElementById('age-verification-modal');
    if (!root || root.dataset.ageBound === '1') {
      this.bindMinicartDeclaration();
      this.syncMinicartCheckoutState();
      return;
    }
    root.dataset.ageBound = '1';

    var _this = this;
    var checkbox = root.querySelector('[data-age-checkbox]');
    var errorEl = root.querySelector('[data-age-error]');
    var textEl = root.querySelector('[data-age-text]');
    var cfg = window.AgeVerificationConfig || {};

    if (textEl && cfg.text) {
      textEl.textContent = cfg.text;
    }

    root.querySelectorAll('[data-age-dismiss]').forEach(function (el) {
      el.addEventListener('click', function () {
        _this.hideModal(false);
      });
    });

    var confirmBtn = root.querySelector('[data-age-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        if (!checkbox || !checkbox.checked) {
          if (errorEl) {
            errorEl.hidden = false;
          }
          return;
        }
        if (errorEl) {
          errorEl.hidden = true;
        }
        confirmBtn.disabled = true;

        var finish = function () {
          _this.hideModal(true);
        };

        if (_this._writeOnConfirm === false) {
          finish();
          return;
        }

        _this
          .recordConsent()
          .then(finish)
          .catch(function (err) {
            console.error('Age verification: failed to save consent', err);
            confirmBtn.disabled = false;
            if (errorEl) {
              errorEl.textContent = 'Something went wrong. Please try again.';
              errorEl.hidden = false;
            }
          });
      });
    }

    if (checkbox) {
      checkbox.addEventListener('change', function () {
        if (checkbox.checked && errorEl) {
          errorEl.hidden = true;
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && _this.isOpen()) {
        _this.hideModal(false);
      }
    });

    this.bindCheckoutGuards();
    this.bindMinicartDeclaration();
    this.syncMinicartCheckoutState();
  },

  isOpen: function () {
    var root = document.getElementById('age-verification-modal');
    return !!(root && !root.hidden);
  },

  formRequiresAgeCheck: function (form) {
    if (!form) {
      return false;
    }
    if (form.getAttribute('data-agecheck') === 'true') {
      return true;
    }
    var host =
      form.closest('[data-agecheck="true"]') ||
      form.closest('product-form-component[data-agecheck="true"]');
    return !!host;
  },

  cartDomNeedsAgeCheck: function (root) {
    var scope = root || document;
    if (scope.querySelector && scope.querySelector('[data-age-restricted="true"]')) {
      return true;
    }
    if (scope.querySelector && scope.querySelector('[data-agecheck="true"]')) {
      return true;
    }
    return false;
  },

  getMinicartCheckbox: function () {
    var miniCart = document.getElementById('mini-cart');
    return miniCart ? miniCart.querySelector('[data-age-minicart-checkbox]') : null;
  },

  hasMinicartDeclaration: function () {
    var miniCart = document.getElementById('mini-cart');
    return !!(miniCart && miniCart.querySelector('[data-age-declaration]'));
  },

  isMinicartConsentChecked: function () {
    var checkbox = this.getMinicartCheckbox();
    return !!(checkbox && checkbox.checked);
  },

  setMinicartCheckoutLocked: function (locked) {
    var miniCart = document.getElementById('mini-cart');
    if (!miniCart) {
      return;
    }
    var gate = miniCart.querySelector('.checkout-se[data-age-gate="true"]');
    if (!gate) {
      return;
    }
    if (locked) {
      gate.setAttribute('data-age-locked', 'true');
    } else {
      gate.removeAttribute('data-age-locked');
    }

    gate.querySelectorAll('a.chek-btn, a.chek-btn-direct').forEach(function (btn) {
      if (locked) {
        btn.classList.add('is-age-locked');
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('tabindex', '-1');
      } else {
        btn.classList.remove('is-age-locked');
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('tabindex');
      }
    });

    var form = miniCart.querySelector('.minicart-form');
    if (form) {
      if (locked) {
        form.removeAttribute('data-age-confirmed');
      } else {
        form.setAttribute('data-age-confirmed', 'true');
      }
    }
  },

  syncMinicartCheckoutState: function () {
    if (!this.hasMinicartDeclaration()) {
      return;
    }
    var _this = this;
    // Prefer live cart attribute over checkbox HTML, in case sync raced.
    this.fetchCart(true)
      .then(function (cart) {
        var confirmed = _this.hasConsentFromCart(cart);
        var checkbox = _this.getMinicartCheckbox();
        if (checkbox) {
          checkbox.checked = confirmed;
        }
        _this.setMinicartCheckoutLocked(!confirmed);
      })
      .catch(function () {
        _this.setMinicartCheckoutLocked(!_this.isMinicartConsentChecked());
      });
  },

  bindMinicartDeclaration: function () {
    if (this._minicartBound) {
      return;
    }
    this._minicartBound = true;
    var _this = this;

    document.body.addEventListener('change', function (e) {
      var checkbox = e.target && e.target.closest && e.target.closest('[data-age-minicart-checkbox]');
      if (!checkbox) {
        return;
      }

      if (checkbox.checked) {
        checkbox.disabled = true;
        _this
          .recordConsent()
          .then(function () {
            checkbox.disabled = false;
            _this.setMinicartCheckoutLocked(false);
            var modalCheckbox = document.querySelector('#age-verification-modal [data-age-checkbox]');
            if (modalCheckbox) {
              modalCheckbox.checked = true;
            }
          })
          .catch(function (err) {
            console.error('Age verification: failed to save minicart consent', err);
            checkbox.checked = false;
            checkbox.disabled = false;
            _this.setMinicartCheckoutLocked(true);
          });
      } else {
        _this.setMinicartCheckoutLocked(true);
      }
    });
  },

  fetchCart: function (force) {
    if (!force && this._cartPromise) {
      return this._cartPromise;
    }
    var _this = this;
    this._cartPromise = fetch(
      typeof PROJ !== 'undefined' && typeof PROJ.cartUrl === 'function'
        ? PROJ.cartUrl('cart.js')
        : '/cart.js',
      {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Failed to load cart');
        }
        return res.json();
      })
      .finally(function () {
        setTimeout(function () {
          _this._cartPromise = null;
        }, 0);
      });
    return this._cartPromise;
  },

  hasConsentFromCart: function (cart) {
    if (!cart || !cart.attributes) {
      return false;
    }
    return cart.attributes[this.ATTR_KEY] === this.ATTR_VALUE;
  },

  recordConsent: function () {
    var _this = this;
    return this.fetchCart(true).then(function (cart) {
      var attrs = {};
      if (cart && cart.attributes) {
        Object.keys(cart.attributes).forEach(function (key) {
          var val = cart.attributes[key];
          // Keep existing attrs; skip null/undefined so we don't re-send noise.
          if (val !== null && val !== undefined) {
            attrs[key] = String(val);
          }
        });
      }
      attrs[_this.ATTR_KEY] = _this.ATTR_VALUE;
      attrs[_this.ATTR_TS_KEY] = new Date().toISOString();

      // form-urlencoded is the most reliable cart Ajax format across themes/apps
      var params = new URLSearchParams();
      Object.keys(attrs).forEach(function (key) {
        params.append('attributes[' + key + ']', attrs[key]);
      });

      return fetch(
        typeof PROJ !== 'undefined' && typeof PROJ.cartUrl === 'function'
          ? PROJ.cartUrl('cart/update.js')
          : '/cart/update.js',
        {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json',
        },
        body: params.toString(),
      }).then(function (res) {
        if (!res.ok) {
          throw new Error('Failed to update cart attributes');
        }
        return res.json();
      }).then(function (updated) {
        if (
          !updated ||
          !updated.attributes ||
          updated.attributes[_this.ATTR_KEY] !== _this.ATTR_VALUE
        ) {
          throw new Error('Cart attributes were not saved');
        }
        return updated;
      });
    });
  },

  showModal: function () {
    var _this = this;
    if (this._pending) {
      return this._pending.promise;
    }

    var root = document.getElementById('age-verification-modal');
    if (!root) {
      return Promise.resolve(false);
    }

    var checkbox = root.querySelector('[data-age-checkbox]');
    var errorEl = root.querySelector('[data-age-error]');
    var confirmBtn = root.querySelector('[data-age-confirm]');

    if (checkbox) {
      checkbox.checked = false;
    }
    if (errorEl) {
      errorEl.textContent = 'Please confirm the declaration to continue.';
      errorEl.hidden = true;
    }
    if (confirmBtn) {
      confirmBtn.disabled = false;
    }

    var resolveFn;
    var promise = new Promise(function (resolve) {
      resolveFn = resolve;
    });
    this._pending = { promise: promise, resolve: resolveFn };

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('age-verification-open');

    if (checkbox) {
      setTimeout(function () {
        checkbox.focus();
      }, 50);
    }

    return promise;
  },

  hideModal: function (confirmed) {
    var wroteConsent = this._writeOnConfirm !== false;
    var root = document.getElementById('age-verification-modal');
    if (root) {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('age-verification-open');

    if (this._pending && typeof this._pending.resolve === 'function') {
      this._pending.resolve(!!confirmed);
    }
    this._pending = null;
    this._writeOnConfirm = true;

    // Only mirror into minicart when consent was already saved with this confirm.
    if (confirmed && wroteConsent) {
      var minicartCheckbox = this.getMinicartCheckbox();
      if (minicartCheckbox) {
        minicartCheckbox.checked = true;
        this.setMinicartCheckoutLocked(false);
      }
    }
  },

  /**
   * Returns a promise that resolves true when cart already has consent,
   * or after the shopper confirms in the modal.
   * By default, writes cart attributes on confirm (checkout safety net).
   * Pass { writeOnConfirm: false } to defer writing (e.g. PDP write after add).
   */
  ensureConsent: function (options) {
    var opts = options || {};
    var writeOnConfirm = opts.writeOnConfirm !== false;
    var _this = this;
    return this.fetchCart(true)
      .then(function (cart) {
        if (_this.hasConsentFromCart(cart)) {
          return true;
        }
        _this._writeOnConfirm = writeOnConfirm;
        return _this.showModal();
      })
      .catch(function (err) {
        console.error('Age verification: cart check failed', err);
        _this._writeOnConfirm = writeOnConfirm;
        return _this.showModal();
      });
  },

  /**
   * If the cart (DOM) has age-restricted lines, ensure consent before proceeding.
   * If no restricted lines, resolves true immediately.
   */
  ensureConsentIfCartRestricted: function (scope) {
    if (!this.cartDomNeedsAgeCheck(scope)) {
      return Promise.resolve(true);
    }
    return this.ensureConsent();
  },

  bindCheckoutGuards: function () {
    var _this = this;

    document.body.addEventListener(
      'click',
      function (e) {
        var checkoutLink = e.target.closest(
          'a.chek-btn[href="/checkout"], a.chek-btn-direct[href="/checkout"], a[href="/checkout"].chek-btn, a[href="/checkout"].chek-btn-direct'
        );
        if (!checkoutLink) {
          return;
        }

        var miniCart = document.getElementById('mini-cart');
        if (!_this.cartDomNeedsAgeCheck(miniCart || document)) {
          return;
        }

        // Inline minicart declaration: block until ticked (no modal).
        if (_this.hasMinicartDeclaration()) {
          if (!_this.isMinicartConsentChecked() || checkoutLink.classList.contains('is-age-locked')) {
            e.preventDefault();
            e.stopPropagation();
            var box = _this.getMinicartCheckbox();
            if (box) {
              box.focus();
            }
            return;
          }
          // Checked — allow normal handlers / navigation.
          return;
        }

        // Shipping-protection "add and checkout" has its own handler; gate there too.
        if (checkoutLink.getAttribute('data-add-and-checkout')) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        _this.ensureConsent().then(function (ok) {
          if (ok) {
            window.location.href = '/checkout';
          }
        });
      },
      true
    );

    // Express / wallet panel — block opening until consent when cart is restricted.
    document.body.addEventListener(
      'click',
      function (e) {
        var openPays = e.target.closest('.openpays');
        if (!openPays) {
          return;
        }
        var miniCart = document.getElementById('mini-cart');
        if (!_this.cartDomNeedsAgeCheck(miniCart || document)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (_this.hasMinicartDeclaration()) {
          if (!_this.isMinicartConsentChecked()) {
            var box = _this.getMinicartCheckbox();
            if (box) {
              box.focus();
            }
            return;
          }
          window.location.href = '/checkout';
          return;
        }
        _this.ensureConsent().then(function (ok) {
          if (ok && miniCart) {
            window.location.href = '/checkout';
          }
        });
      },
      true
    );

    // Horizon cart page checkout button (if ever shown).
    document.body.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('button.cart__checkout-button[name="checkout"], #checkout');
        if (!btn) {
          return;
        }
        var cartRoot =
          btn.closest('cart-items-component') ||
          btn.closest('.cart') ||
          document.getElementById('main-cart') ||
          document;
        if (!_this.cartDomNeedsAgeCheck(cartRoot)) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        _this.ensureConsent().then(function (ok) {
          if (ok) {
            window.location.href = '/checkout';
          }
        });
      },
      true
    );
  },
};

document.addEventListener('DOMContentLoaded', function () {
  PROJ.ageVerification.init();
});
